import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import HeaderOrg from "../Headers/HeaderOrg";
import Loader from "../../Components/Loader/Loader";
import { ShowToastMessage } from "../../helper/ShowToastMessage";
import { serviceConsumer } from "../../network/ServiceConsumer";
import {
  getTabulationStatus,
  getPanels,
  setPanelSlot,
  getScoreGrid,
  enterScores,
  moveSheet,
  setMishap,
  recordSignOff,
  getResults,
  getResultsPreview,
  checkResult,
  drawRoundAward,
  publishRecall,
  publishResults,
  setNaidcExtra,
  downloadEventDayPdf,
  errorCode,
} from "../../network/oireachtasEventDay";
import { useMaterialSymbols, Msr } from "../OireachtasPanel/symbols";
import { useJobDevice } from "../../device/DeviceContext";
import useAutoRefresh from "./useAutoRefresh";
import { ordinal } from "../AwardsTable/awardsSearch";
import {
  compStep,
  preRecallRounds,
  defaultRound,
  stepLabel,
  roundOpen,
  roundPublished,
  stageChecked,
  sheetState,
  SHEET_LABEL,
  boxesFromGrid,
  boxesChanged,
  sheetFingerprint,
  sheetProblems,
  blanks,
  sheetChanges,
  draftKey,
  mergeDraft,
  readDraft,
  writeDraft,
  dropDraft,
  listDrafts,
  sheetPages,
  scheduledToday,
  eventDayKey,
} from "./tabulationFlow";
import "../OireachtasPanel/oireachtas.css";
import "../OireachtasPanel/organizerOireachtas.css";
import "./oireachtasCheckIn.css";
import "./oireachtasTabulation.css";
import { FullTable } from "./OireachtasResultsTable.jsx";
import { droppedSlots } from "./oireachtasResultsModel";

// View Scores / Enter Scores (Devin 2026-10-21, reshaped 2026-10-28). One
// competition at a time:
//   pick   — View Scores, the competition's hub: competition (today's on the
//            stage schedule), Round / Recall tabs; a round's tab is every
//            judge's scores side by side, each judge's sheet state and its
//            sign-off actions (print the sign-off sheet, mark it signed) under
//            their name, a name opening that sheet to edit, and the judges
//            still to enter; the Recall tab and the round tabs carry the next
//            stage (print + publish the recall; print, announce, publish)
//   entry  — the score boxes laid out like the judge's paper sheet, in
//            running order (every dancer due; check-in plays no part — a
//            blank box means did not dance); Enter moves to the next box;
//            nothing is stored yet
//   confirm — the typed sheet, in the same layout, to check against the
//            paper: Back to edit, or Confirm, which stores it (claiming the
//            judge's seat if new) and returns to View Scores
// A judge may sit any round (ECRO 2026: round 3 is a mix of the round 1 and 2
// judges); only one seat per round. Typed scores are never dropped silently:
// leaving with unsaved scores asks first. Whole-event progress stays on the
// Status screen.

// Round awards belong to the multi-round solo championships (backend
// helpers/oireachtasRecalls.roundAwardsApply; keep the two in step).
const roundAwardsApply = (c) =>
  ["solo_champ", "adult_solo", "advanced_adult"].includes(c?.type) && (Number(c?.rounds) || 1) >= 2;

const errorText = (e, fallback) => e?.messages || e?.message || fallback;
// the full results table highlights nobody here (no viewer of their own)
const NO_CARDS = new Set();

const clockLabel = (d) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const OireachtasTabulation = () => {
  useMaterialSymbols();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const eventId = params.get("id");
  // Two doors to one screen (Devin 2026-10-28): Enter Scores (?mode=enter)
  // goes straight to picking a competition, round and judge to type a sheet;
  // View Scores (the default) is the competition's hub. A saved sheet always
  // lands on View Scores. The address decides which, so the browser's Back
  // and a reload always match the screen (review d6198824 R1-F3).
  const entering = params.get("mode") === "enter";

  const [event, setEvent] = useState(null);
  const [rows, setRows] = useState([]); // Status rows, one per competition
  const [todayIds, setTodayIds] = useState(null); // competitions on today's schedule; null = list all
  const [showAll, setShowAll] = useState(false); // the tabulator lifted the today filter
  const todayOnly = !!todayIds && !showAll;
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [compId, setCompId] = useState("");
  const [round, setRound] = useState(1);
  const [recallView, setRecallView] = useState(false); // the Recall tab instead of a round's table
  // the Recall tab is View Scores only: Back into Enter Scores shows the round
  // (review d6198824 R2-F1)
  const showRecall = recallView && !entering;
  // the Results tab (Devin 2026-10-28): the full table as the public results
  // page shows it, then the check, the prints and Publish
  const [resultsView, setResultsView] = useState(false);
  const showResults = resultsView && !entering;
  const [preview, setPreview] = useState(null); // { compId, data } | { compId, error }
  const [grid, setGrid] = useState(null);
  const [pool, setPool] = useState([]); // the event's adjudicators
  const [panels, setPanels] = useState([]); // seats per round
  const [person, setPerson] = useState("");
  const [boxes, setBoxes] = useState([]);
  const [original, setOriginal] = useState([]);
  const [phase, setPhase] = useState("pick");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [publishAsk, setPublishAsk] = useState(""); // "recall" | "results"
  // the overall result shown for a tabulator to check: { compId, stage, data }
  const [overall, setOverall] = useState(null);
  const overallReq = useRef(0); // the latest request; older answers are ignored
  // changing a published round: the admin confirms the correction first
  const [correctionAsk, setCorrectionAsk] = useState(false);
  // On an event-day device (card 540f21a4) the job decides what shows:
  // checking and printing the overall result, and publishing. Correcting a
  // published result is never a device's (unpublish first, then edit).
  const { device: jobDevice, can } = useJobDevice();
  const canCheck = can("ox.results.check");
  const [correctionOk, setCorrectionOk] = useState(false);
  const boxRefs = useRef([]);
  const discardedRef = useRef(false); // Back confirmed "discard": don't ask again on the way out
  const [notice, setNotice] = useState(""); // e.g. a restored draft
  const draftKeyRef = useRef(null); // the open sheet's draft key
  const originalRef = useRef([]); // the saved sheet, for a discard from the Back prompt
  originalRef.current = original;
  // the judge's sheet as it was when typing started (sheetFingerprint): Save
  // is refused if another tabulator saved it since (Devin 2026-10-22)
  const baseSheetRef = useRef("");
  const [changeTo, setChangeTo] = useState(null); // Change judge: null = closed, "" = open

  const row = rows.find((r) => String(r.competition._id) === String(compId));
  const comp = row?.competition;
  const seatOf = (personId, r = round) =>
    ((panels.find((p) => Number(p.round) === Number(r)) || {}).slots || []).find(
      (s) => String(s.person_id) === String(personId)
    )?.slot || "";
  const slot = seatOf(person);
  const judgeName = (personId) => pool.find((a) => String(a._id) === String(personId))?.name || "";
  const dirty = (phase === "entry" && boxesChanged(boxes, original)) || phase === "confirm";

  // ---- loading ---------------------------------------------------------------
  // The newest load of each kind wins: a slower, older answer never replaces
  // a newer one. The automatic refresh (below) also checks these.
  const statusSeq = useRef(0);
  const sheetSeq = useRef(0);
  const refreshStatus = useCallback(async () => {
    const seq = ++statusSeq.current;
    const res = await getTabulationStatus(eventId);
    if (seq === statusSeq.current) setRows(res.rows || []);
    return res.rows || [];
  }, [eventId]);

  const loadSheet = async (cid, r) => {
    const seq = ++sheetSeq.current;
    const [g, p] = await Promise.all([getScoreGrid(eventId, cid, r), getPanels(eventId, cid)]);
    if (seq === sheetSeq.current) {
      setGrid(g);
      setPool(p?.adjudicators || []);
      setPanels(p?.panels || []);
    }
    return { grid: g, panels: p?.panels || [] };
  };

  // ---- the automatic refresh (Devin 2026-11-02, ECRO dress rehearsal) ----------
  // View Scores and the Enter Scores picker re-read every 10 seconds, so the
  // head tabulator sees other tabulators' sheets and sign-offs, and a
  // tabulator waiting for round 3 sees it open when the recall is published.
  // Never while a sheet is being typed or confirmed (phase "entry"/"confirm"),
  // never while an action runs or a confirmation is open. A read that started
  // before any of those (or before another competition, round or tab was
  // opened) is dropped when it answers: pollGen changes synchronously with
  // each of them (plan review 7631d72b R1-F1). Nothing is set when nothing
  // changed, so the screen doesn't redraw every 10 seconds.
  const pollGen = useRef(0);
  const startBusy = () => {
    pollGen.current += 1;
    setBusy(true);
  };
  // a change to the open competition seen by the refresh: the results read
  // from it (overall result, recall, after publication) are read again (R1-F2)
  const [liveVersion, setLiveVersion] = useState(0);
  // one of those reads failed: the next tick asks again even if nothing
  // changed (plan review 7631d72b R2-F2)
  const liveRetry = useRef(false);
  // only for a failure that may pass (no answer, a server error): a refusal
  // (4xx, e.g. a job without the function) is not retried every 10 seconds
  // (code review ac80e4a0 R1-F1)
  const retryLater = (e) => {
    const code = e?.error?.response?.status;
    if (!(code >= 400 && code < 500)) liveRetry.current = true;
  };
  // result reads on their way (overall result, preview, points, recall, after
  // publication): the refresh waits for them, so a slow one is never piled on
  // by the next tick (code review 86bb9e53 R1-F1)
  // (held until each answers, however long: code review 86bb9e53 R2)
  const resultReads = useRef(0);
  const trackRead = (p) => {
    resultReads.current += 1;
    return Promise.resolve(p).finally(() => {
      resultReads.current -= 1;
    });
  };
  const pollActive = !loading && !loadError && phase === "pick";
  const pollPaused = busy || !!publishAsk || correctionAsk || changeTo !== null;
  const eligibleRef = useRef(false);
  eligibleRef.current = pollActive && !pollPaused;
  const shownRef = useRef({});
  shownRef.current = { rows, grid, panels, pool, compId, round };
  const pollTick = async () => {
    if (resultReads.current > 0) return false; // skipped: not a refresh
    const gen = pollGen.current;
    const statusAt = statusSeq.current;
    const sheetAt = sheetSeq.current;
    const { compId: cid, round: rnd, rows: rowsThen } = shownRef.current;
    const open = rowsThen.find((r) => String(r.competition._id) === String(cid));
    const withSheet = !!cid && !!open?.draw?.locked;
    // every read settles before this run ends, so a failed one can't free
    // the next tick while the others are still on their way (plan review
    // acff1d55 R1-F1)
    const settled = await Promise.allSettled([
      getTabulationStatus(eventId),
      ...(withSheet ? [getScoreGrid(eventId, cid, rnd), getPanels(eventId, cid)] : []),
    ]);
    const failed = settled.find((x) => x.status === "rejected");
    if (failed) throw failed.reason;
    const [res, g0, p0] = settled.map((x) => x.value);
    const sheet = withSheet ? [g0, p0] : null;
    if (gen !== pollGen.current || !eligibleRef.current) return;
    const now = shownRef.current;
    if (String(now.compId) !== String(cid) || Number(now.round) !== Number(rnd)) return;
    const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
    const rowOf = (list) => (list || []).find((r) => String(r.competition._id) === String(cid)) || null;
    let changed = false;
    if (statusAt === statusSeq.current) {
      const fresh = res.rows || [];
      if (!same(fresh, now.rows)) {
        if (cid && !same(rowOf(fresh), rowOf(now.rows))) changed = true;
        setRows(fresh);
      }
    }
    if (sheet && sheetAt === sheetSeq.current) {
      const [g, p] = sheet;
      if (!same(g, now.grid)) {
        setGrid(g);
        changed = true;
      }
      if (!same(p?.panels || [], now.panels)) setPanels(p?.panels || []);
      if (!same(p?.adjudicators || [], now.pool)) setPool(p?.adjudicators || []);
    }
    if (changed || liveRetry.current) {
      liveRetry.current = false;
      setLiveVersion((v) => v + 1);
    }
  };
  const refreshed = useAutoRefresh(pollTick, { everyMs: 10 * 1000, active: pollActive, paused: pollPaused });

  useEffect(() => {
    (async () => {
      try {
        const [eventRes, statusRows, scheduleRes] = await Promise.all([
          serviceConsumer("GET", `${process.env.REACT_APP_BASE_URL}/oireachtas/${eventId}`),
          refreshStatus(),
          // no schedule (or no access to it) just means every competition is listed
          serviceConsumer("GET", `${process.env.REACT_APP_BASE_URL}/oireachtas/${eventId}/schedule`).catch(() => null),
        ]);
        setEvent(eventRes.event);
        setTodayIds(
          scheduledToday(
            scheduleRes?.schedule,
            eventDayKey(scheduleRes?.event?.timeZone || eventRes.event?.timeZone)
          )
        );
        // Deep link from the Status screen: ?comp=<id> opens that competition.
        const compParam = params.get("comp");
        const linked = statusRows.find((r) => String(r.competition._id) === compParam);
        if (linked) await openComp(linked);
      } catch (e) {
        setLoadError(errorText(e, "Could not load tabulation"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // Leaving the page (reload, close) with typed scores asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      if (discardedRef.current) return; // already confirmed on Back
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // The browser's own Back / Forward don't fire beforeunload inside the app,
  // and this router (BrowserRouter) has no navigation blocker. While scores
  // are typed, a copy of this page sits on top of the history, and any
  // history move asks first. The listener runs in the capture phase, ahead
  // of the router's own (which would re-render to the other page before a
  // later listener ran): staying stops the router from seeing the move and
  // puts this page back on top; leaving lets the move through (Back onto the
  // copy's twin — the same page — then goes back for real).
  useEffect(() => {
    if (!dirty) return undefined;
    discardedRef.current = false; // a new round of typing: ask again on the way out
    const here = window.location.pathname + window.location.search;
    const sentinel = () => window.history.pushState({ ...(window.history.state || {}), tbUnsaved: true }, "", here);
    sentinel();
    const onPopState = (e) => {
      const samePage = window.location.pathname + window.location.search === here;
      if (window.confirm("Discard the scores typed for this judge? They have not been saved.")) {
        window.removeEventListener("popstate", onPopState, true);
        discardedRef.current = true;
        if (draftKeyRef.current) dropDraft(window.localStorage, draftKeyRef.current);
        // discarded means discarded: if this page stays (an older entry of
        // the same page), the sheet is back to what is saved and the guard
        // arms again with the next edit
        setBoxes(originalRef.current);
        if (samePage) window.history.back();
        return;
      }
      e.stopImmediatePropagation();
      sentinel();
    };
    window.addEventListener("popstate", onPopState, true);
    return () => window.removeEventListener("popstate", onPopState, true);
  }, [dirty]);

  const guard = (fn) => () => {
    if (dirty) {
      if (!window.confirm("Discard the scores typed for this judge? They have not been saved.")) return;
      if (draftKeyRef.current) dropDraft(window.localStorage, draftKeyRef.current);
    }
    fn();
  };

  // ---- navigation between steps ----------------------------------------------
  const openComp = async (r, rnd) => {
    pollGen.current += 1;
    const cid = String(r.competition._id);
    const target = rnd || defaultRound(r);
    setCompId(cid);
    setRound(target);
    // a competition waiting on its recall opens on the Recall tab
    const opening = compStep(r).step;
    setRecallView(!entering && !rnd && opening === "recall");
    // a competition whose sheets are all signed opens on its Results tab
    setResultsView(!entering && !rnd && (opening === "results" || opening === "done"));
    setPerson("");
    setPhase("pick");
    setError("");
    setGrid(null);
    if (!r.draw?.locked) return;
    try {
      startBusy();
      await loadSheet(cid, target);
    } catch (e) {
      setError(errorText(e, "Could not load the competition"));
    } finally {
      setBusy(false);
    }
  };

  const pickComp = (cid) =>
    guard(() => {
      const r = rows.find((x) => String(x.competition._id) === cid);
      if (r) openComp(r);
      else {
        pollGen.current += 1;
        setCompId("");
        setGrid(null);
        setPhase("pick");
      }
    })();

  const pickRound = (r) => guard(() => openComp(row, r))();
  // to the hub, by its address (Back returns to Enter Scores). The copy of
  // this page that the unsaved-scores guard put on the history is replaced,
  // not stacked on.
  const showHub = () => {
    pollGen.current += 1;
    navigate(`/oireachtas/tabulation?id=${eventId}${compId ? `&comp=${compId}` : ""}`, {
      replace: !!window.history.state?.tbUnsaved,
    });
  };
  // the Recall tab: the recall's check / print / publish, between rounds 2 and 3
  const pickResults = () =>
    guard(() => {
      pollGen.current += 1;
      setPerson("");
      setPhase("pick");
      setError("");
      setRecallView(false);
      setResultsView(true);
    })();
  const pickRecall = () =>
    guard(() => {
      pollGen.current += 1;
      setPerson("");
      setPhase("pick");
      setError("");
      setRecallView(true);
      setResultsView(false);
    })();

  const pickJudge = (personId) => {
    pollGen.current += 1;
    const seat = seatOf(personId);
    const fresh = boxesFromGrid(grid, seat);
    const key = draftKey(eventId, compId, round, personId);
    const draft = readDraft(window.localStorage, key);
    draftKeyRef.current = key;
    const refByCard = Object.fromEntries(fresh.map((b) => [b.card, b.ref]));
    baseSheetRef.current = draft
      ? draft.baseSheet ?? sheetFingerprint(draft.base, refByCard)
      : sheetFingerprint(fresh);
    setPerson(personId);
    setBoxes(draft ? mergeDraft(fresh, draft.boxes, draft.base) : fresh);
    setOriginal(fresh);
    setChangeTo(null);
    setError("");
    setNotice(
      draft
        ? `Restored the scores typed earlier for ${judgeName(personId)} — they are not saved yet. Check them against the sheet and Save.`
        : ""
    );
    setPhase("entry");
  };

  // Keep typed, unconfirmed scores in the browser until they are saved or
  // discarded, so a page change, reload or crash can't lose them.
  useEffect(() => {
    const key = draftKeyRef.current;
    if (!key || !person || (phase !== "entry" && phase !== "confirm")) return;
    if (boxesChanged(boxes, original)) {
      writeDraft(window.localStorage, key, {
        code: comp?.code,
        round,
        judge: judgeName(person),
        boxes: boxes.map((b) => ({ card: b.card, score: b.score, ftc: b.ftc })),
        // the sheet as it was when this typing started: restore only changes
        base: original.map((b) => ({ card: b.card, score: b.score, ftc: b.ftc })),
        baseSheet: baseSheetRef.current,
        at: Date.now(),
      });
    } else {
      dropDraft(window.localStorage, key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxes, original, phase, person]);

  // A judge's sheet opens with the cursor in the first box, so typing can
  // start straight away. That first focus doesn't centre the box: the page
  // scrolls to the sheet's heading instead, so the heading, any "already
  // published" warning and the first box are on screen wherever the page was
  // (reviews a02c3146 R1-F3, f192e5a1 R1-F2); Tab, Enter or a click centre.
  const openingFocusRef = useRef(false);
  const sheetHeadRef = useRef(null);
  useEffect(() => {
    if (phase === "entry" && boxRefs.current[0]) {
      if (sheetHeadRef.current) sheetHeadRef.current.scrollIntoView({ block: "start" });
      openingFocusRef.current = true;
      boxRefs.current[0].focus({ preventScroll: true });
      openingFocusRef.current = false;
    }
  }, [phase, person]);

  const backToPick = async () => {
    setPerson("");
    setPhase("pick");
    setError("");
    try {
      startBusy();
      await Promise.all([loadSheet(compId, round), refreshStatus()]);
    } catch (e) {
      setError(errorText(e, "Could not reload the competition"));
    } finally {
      setBusy(false);
    }
  };

  // ---- entry -------------------------------------------------------------------
  // The box being typed into sits mid-screen (Devin 2026-10-28): Tab or Enter
  // down the sheet never leaves it at the edge or under the fixed bottom bar
  // (which the browser's own focus scroll ignores). Sideways only as needed
  // (a phone scrolls the sheet sideways).
  const centreBox = (el) => {
    try {
      el.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
    } catch (e) {
      el.scrollIntoView(false);
    }
  };
  const editBox = (i, patch) => setBoxes((prev) => prev.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const onBoxKey = (i) => (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (i + 1 < boxes.length) boxRefs.current[i + 1] && boxRefs.current[i + 1].focus();
    else toConfirm();
  };
  const toConfirm = () => {
    setError("");
    setPhase("confirm");
  };

  // ---- confirm: store the sheet -----------------------------------------------
  const confirmSheet = async (correct = false) => {
    const { upserts, clears, ftc } = sheetChanges(boxes, original);
    const changing = upserts.length || clears.length || ftc.length;
    // a real change to a round already public is a correction: warn first
    // (the server refuses it without the confirmation anyway)
    if (!correct && changing && boxesChanged(boxes, original) && roundPublished(row, round)) {
      setCorrectionOk(false);
      setCorrectionAsk(true);
      return;
    }
    // What is stored so far, updated after each write that succeeds: if a
    // later step fails, the next Confirm compares against what the server
    // really has (an erased score is still sent as a clear).
    let committed = original;
    try {
      startBusy();
      setError("");
      let seat = slot;
      if (!seat) {
        // a judge new to this round takes the first free seat now, with the
        // scores — even with none to save (nobody danced): a sheet prints
        // only with its judge seated
        const fresh = await getPanels(eventId, compId);
        const taken = ((fresh?.panels || []).find((p) => Number(p.round) === Number(round)) || {}).slots || [];
        const mine = taken.find((s) => String(s.person_id) === String(person));
        seat = mine ? mine.slot : (grid.competition.judgeSlots || []).find((s) => !taken.some((t) => t.slot === s));
        if (!seat) throw new Error("This round's panel is already full");
        if (!mine) await setPanelSlot(eventId, compId, round, seat, person);
      }
      // always sent, even with nothing to write: the server checks nobody
      // else saved this judge's sheet since typing started
      await enterScores(eventId, compId, round, seat, [...upserts, ...clears], person, correct, baseSheetRef.current);
      if (upserts.length || clears.length) {
        const typed = Object.fromEntries(boxes.map((b) => [b.card, b.score]));
        committed = committed.map((b) => ({ ...b, score: typed[b.card] ?? b.score }));
      }
      baseSheetRef.current = sheetFingerprint(committed);
      for (const m of ftc) {
        await setMishap(
          eventId,
          compId,
          {
            ...(m.ftc ? { round, ref: m.ref, type: "failed_to_complete", zeroed: true } : { round, ref: m.ref, clear: true }),
            ...(correct ? { correctPublished: true } : {}),
          }
        );
        committed = committed.map((b) => (b.ref === m.ref ? { ...b, ftc: m.ftc } : b));
      }
      setCorrectionAsk(false);
      // the sheet is stored; a failed refresh is retried by Confirm (which
      // then has nothing left to write)
      setError("");
      try {
        const { panels: p } = await loadSheet(compId, round);
        await refreshStatus();
        setPanels(p);
      } catch (refreshError) {
        setError("The scores are saved, but the screen could not refresh — check the connection and press Confirm again.");
        return;
      }
      if (draftKeyRef.current) dropDraft(window.localStorage, draftKeyRef.current);
      setNotice("");
      // saved: back to View Scores, where the sheet is printed and signed off
      // (Devin 2026-10-28: no separate sign-off screen)
      ShowToastMessage("success", `${judgeName(person)}'s round ${round} sheet is saved — print the sign-off sheet from the table`);
      setPerson("");
      setRecallView(false);
      setResultsView(false);
      showHub();
      setPhase("pick");
    } catch (e) {
      if (errorCode(e) === "PUBLISHED_CORRECTION") {
        // published since this screen loaded: ask, as above
        setCorrectionOk(false);
        setCorrectionAsk(true);
      } else {
        setCorrectionAsk(false);
        setError(errorText(e, "The scores could not be saved — try Confirm again"));
      }
    } finally {
      setOriginal(committed);
      setBusy(false);
    }
  };

  // ---- Change judge -------------------------------------------------------------
  // The paper belongs to another judge (Devin 2026-10-22). A sheet not saved
  // yet just takes the other judge, typed scores and all. A saved sheet moves
  // to the other judge on the server, which frees this judge for the
  // tabulator whose save was refused.
  const savedHere = !!slot && original.some((b) => b.score.trim() !== "");
  const freeJudges = pool.filter((a) => !seatOf(a._id));
  const changeJudge = async () => {
    const to = changeTo;
    if (!to) return;
    const fromName = judgeName(person);
    try {
      startBusy();
      setError("");
      if (savedHere) {
        if (boxesChanged(boxes, original)) {
          setError("Save or undo the changes on this sheet before moving it to another judge.");
          return;
        }
        await moveSheet(eventId, compId, round, slot, to);
        await Promise.all([loadSheet(compId, round), refreshStatus()]);
        setNotice(`Moved to ${judgeName(to)} — ${fromName} is free again.`);
      } else {
        // the typed scores go with the other judge, who has no sheet yet —
        // unless scores typed for that judge are waiting on this computer
        if (readDraft(window.localStorage, draftKey(eventId, compId, round, to))) {
          setError(`Unsaved scores for ${judgeName(to)} are waiting on this computer — open ${judgeName(to)}'s sheet and save or discard them first.`);
          return;
        }
        const fresh = boxesFromGrid(grid, "");
        baseSheetRef.current = sheetFingerprint(fresh);
        setOriginal(fresh);
        setNotice(`Now ${judgeName(to)}'s sheet — nothing is saved yet.`);
      }
      if (draftKeyRef.current) dropDraft(window.localStorage, draftKeyRef.current);
      draftKeyRef.current = draftKey(eventId, compId, round, to);
      setPerson(to);
      setChangeTo(null);
    } catch (e) {
      setError(errorText(e, "The sheet could not be moved"));
    } finally {
      setBusy(false);
    }
  };
  const changeJudgeBar = () =>
    changeTo === null ? (
      <button type="button" className="op-btn" disabled={busy} onClick={() => setChangeTo("")}>
        <Msr>swap_horiz</Msr> Change judge
      </button>
    ) : (
      <div className="tb-change">
        <span>{savedHere ? "Move this saved sheet to" : "This sheet is for"}</span>
        <select className="tb-select" value={changeTo} onChange={(e) => setChangeTo(e.target.value)}>
          <option value="">Choose the judge…</option>
          {freeJudges.map((a) => (
            <option key={a._id} value={String(a._id)}>
              {a.name}
            </option>
          ))}
        </select>
        <button type="button" className="op-btn go" disabled={busy || !changeTo} onClick={changeJudge}>
          {savedHere ? "Move" : "Change"}
        </button>
        <button type="button" className="op-btn" disabled={busy} onClick={() => setChangeTo(null)}>
          Cancel
        </button>
      </div>
    );

  // ---- sign-off (from a judge's column on View Scores) ---------------------------
  const printSheet = async (seatSlot, personId) => {
    try {
      startBusy();
      setError("");
      await downloadEventDayPdf(
        eventId,
        compId,
        `verification.pdf?round=${round}&judgeSlot=${seatSlot}`,
        `${comp.code}-round${round}-${judgeName(personId).replace(/\s+/g, "-") || seatSlot}-sign-off.pdf`
      );
      await Promise.all([loadSheet(compId, round), refreshStatus()]);
    } catch (e) {
      setError(errorText(e, "The sign-off sheet could not be printed"));
    } finally {
      setBusy(false);
    }
  };
  const markSigned = async (seatSlot) => {
    try {
      startBusy();
      setError("");
      await recordSignOff(eventId, compId, round, seatSlot);
      await Promise.all([loadSheet(compId, round), refreshStatus()]);
    } catch (e) {
      setError(errorText(e, "The sign-off could not be recorded"));
    } finally {
      setBusy(false);
    }
  };

  // ---- the competition's next step: recall / results ---------------------------
  // sheet "announcer": the awards pages (World qualifiers, then places from
  // last to first with the NAIDC line — no scores); otherwise the full results
  const printStage = async (stage, sheet = "") => {
    try {
      startBusy();
      setError("");
      await downloadEventDayPdf(
        eventId,
        compId,
        `announce.pdf?stage=${stage}${sheet ? `&sheet=${sheet}` : ""}`,
        `${comp.code}-${sheet === "roundawards" ? `round-awards-${stage}` : sheet || stage}.pdf`
      );
    } catch (e) {
      setError(errorText(e, "The list could not be printed"));
    } finally {
      setBusy(false);
    }
  };
  // a recall round's check-in sheet and musician sheet (Devin 2026-11-03):
  // printed with its score sheets, recalled dancers only
  const printRoundSheet = async (kind, r) => {
    const [file, what] = kind === "musician" ? ["musician", "musician sheet"] : ["checkin", "check-in sheet"];
    try {
      startBusy();
      setError("");
      await downloadEventDayPdf(eventId, compId, `${file}.pdf?round=${r}`, `${comp.code}-${file}-r${r}.pdf`);
    } catch (e) {
      setError(errorText(e, `The ${what} could not be printed`));
    } finally {
      setBusy(false);
    }
  };
  // a recall round's judging sheets: printed once the recall is published,
  // with the recalled numbers only (Devin 2026-10-28)
  const printScoreSheets = async (r) => {
    try {
      startBusy();
      setError("");
      await downloadEventDayPdf(eventId, compId, `scoresheets.pdf?round=${r}`, `${comp.code}-scoresheets-r${r}.pdf`);
    } catch (e) {
      setError(errorText(e, "The score sheets could not be printed"));
    } finally {
      setBusy(false);
    }
  };
  // the job is re-read every minute: an open confirmation closes when its
  // publish function is unticked (MC review c87cf2d7 R3-F2)
  const publishFnAllowed = !publishAsk || can(`ox.${publishAsk}.publish`);
  useEffect(() => {
    if (!publishFnAllowed) setPublishAsk("");
  }, [publishFnAllowed]);
  const publish = async (stage) => {
    if (!can(`ox.${stage}.publish`)) {
      setPublishAsk("");
      return;
    }
    try {
      startBusy();
      setError("");
      if (stage === "recall") {
        await publishRecall(eventId, compId);
        ShowToastMessage("success", `Recall published — ${comp.code}`);
      } else {
        await publishResults(eventId, compId);
        ShowToastMessage("success", `Results published — ${comp.code}`);
      }
      const fresh = await refreshStatus();
      const r = fresh.find((x) => String(x.competition._id) === String(compId));
      if (r) await openComp(r);
    } catch (e) {
      setError(errorText(e, "Publishing failed"));
    } finally {
      setPublishAsk("");
      setBusy(false);
    }
  };

  // ---- the tabulator's check of the overall result ----------------------------
  // Once a stage's sheets are all signed, the overall result is shown; a
  // tabulator checks it and signs it off, which unlocks Print and Publish.
  // For the recall, the round-award judges (one drawn at random for each of
  // rounds 1 and 2) are drawn BEFORE the result is shown, so the tabulator
  // sees the award winners they are checking.
  // keep: a reload after the refresh saw a change keeps the shown result on
  // screen until the new one arrives
  const loadOverall = async (cid, stage, { keep = false } = {}) => {
    const req = ++overallReq.current;
    if (!keep) setOverall(null);
    try {
      let data = await getResults(eventId, cid);
      if (stage === "recall" && data.roundAwardsApply) {
        const drawn = new Set((data.roundAwardDraws || []).map((d) => d.round));
        const missing = [1, 2].filter((r) => !drawn.has(r));
        if (missing.length) {
          let failed = null;
          for (const r of missing) {
            try {
              await drawRoundAward(eventId, cid, r);
            } catch (e) {
              failed = e; // another tabulator may have drawn it meanwhile
            }
          }
          data = await getResults(eventId, cid);
          const now = new Set((data.roundAwardDraws || []).map((d) => d.round));
          if (failed && [1, 2].some((r) => !now.has(r))) throw failed;
        }
      }
      if (req === overallReq.current) setOverall({ compId: cid, stage, data });
    } catch (e) {
      if (req === overallReq.current) {
        retryLater(e);
        setOverall(null);
        setError(errorText(e, "Could not load the overall result"));
      }
    }
  };
  const readyStage = row ? compStep(row).step : "";
  const overallLive = useRef(liveVersion);
  useEffect(() => {
    const keep = overallLive.current !== liveVersion;
    overallLive.current = liveVersion;
    if (canCheck && compId && (readyStage === "recall" || readyStage === "results")) trackRead(loadOverall(compId, readyStage, { keep }));
    else {
      overallReq.current += 1;
      setOverall(null);
    }
    // canCheck: the job is re-read every minute (MC review c87cf2d7 R2-F4)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compId, readyStage, row?.checks?.recall?.checked, row?.checks?.results?.checked, canCheck, liveVersion]);

  // The Results tab's full table, once every sheet is signed (the live result)
  // or the results are published (the published snapshot). Reloaded with the
  // checked result and after publishing, so the table is what gets checked.
  const previewReq = useRef(0);
  const resultsReady = readyStage === "results" || readyStage === "done";
  // a refresh's change reaches this table through the overall result when
  // one is being checked (it reloads first, then this): read it once, not
  // twice (code review ac80e4a0 R1-F2). After publication there is no overall
  // result to wait for, so the change triggers this directly.
  const previewLive = canCheck && readyStage === "results" ? 0 : liveVersion;
  useEffect(() => {
    const req = ++previewReq.current;
    if (entering || !compId || !resultsReady) {
      setPreview(null);
      return;
    }
    setPreview(null);
    // the overall result this table was loaded after: the check (which sends
    // that result's version) waits for a table loaded after it, so a sign-off
    // is never for a table that hasn't appeared (MC review R1-F1)
    const after = overall;
    trackRead(getResultsPreview(eventId, compId))
      .then((data) => req === previewReq.current && setPreview({ compId: String(compId), data, after }))
      .catch((e) => {
        if (req !== previewReq.current) return;
        retryLater(e);
        setPreview({ compId: String(compId), error: errorText(e, "Could not load the results") });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compId, resultsReady, entering, row?.checks?.results?.checked, row?.resultsPublished, overall, previewLive]);
  const previewFor = preview && preview.compId === String(compId) ? preview : null;
  const previewShown = !!(previewFor && previewFor.data && previewFor.after === overall);

  // The Recall tab after the recall is published: its table from the live
  // result (the same recall the publish stored, unless corrected since).
  const [recallData, setRecallData] = useState(null); // { compId, data }
  // a round tab's Irish points (the live result), loaded with its grid
  const [roundResults, setRoundResults] = useState(null); // { compId, grid, data }
  const roundTab = phase === "pick" && !entering && !showRecall && !showResults;
  useEffect(() => {
    if (!roundTab || !compId || !grid) return undefined;
    let live = true;
    // tagged with this grid load: a reloaded grid shows no points until its
    // own result arrives, never the previous scores' (MC review R1-F2)
    const forGrid = grid;
    trackRead(getResults(eventId, compId))
      .then((data) => live && setRoundResults({ compId: String(compId), grid: forGrid, data }))
      .catch((e) => {
        if (!live) return;
        retryLater(e);
        setRoundResults(null); // the scores still show; only the points are missing
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundTab, compId, grid, liveVersion]);
  // after the results are published: the ranked round awards stay on screen
  const [doneData, setDoneData] = useState(null); // { compId, data }
  useEffect(() => {
    if (!canCheck || !compId || readyStage !== "done") return undefined;
    let live = true;
    trackRead(getResults(eventId, compId))
      .then((data) => live && setDoneData({ compId: String(compId), data }))
      .catch((e) => {
        if (!live) return;
        retryLater(e);
        setDoneData(null);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCheck, compId, readyStage, row?.resultsPublished, liveVersion]);
  const recallOut = !!row?.recallPublished && readyStage !== "recall";
  useEffect(() => {
    if (!showRecall || !recallOut || !compId) return undefined;
    let live = true;
    trackRead(getResults(eventId, compId))
      .then((data) => live && setRecallData({ compId: String(compId), data }))
      .catch((e) => {
        if (!live) return;
        retryLater(e);
        setError(errorText(e, "Could not load the recall"));
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRecall, recallOut, compId, liveVersion]);

  // the shown result belongs to this competition and stage
  const overallFor = (stage) =>
    overall && overall.compId === String(compId) && overall.stage === stage ? overall.data : null;

  const checkStage = async (stage) => {
    const shown = overallFor(stage);
    if (!shown) return;
    try {
      startBusy();
      setError("");
      await checkResult(eventId, compId, stage, shown.versions && shown.versions[stage]);
      await refreshStatus();
    } catch (e) {
      setError(errorText(e, "The check could not be recorded"));
      // whatever changed, show the current result before another check
      loadOverall(compId, stage);
    } finally {
      setBusy(false);
    }
  };

  // ---- rendering ---------------------------------------------------------------
  const step = row ? compStep(row) : null;
  const scored = boxes.filter((b) => b.score.trim() !== "").length;
  const problems = sheetProblems(boxes);
  const badCards = new Set(problems.map((p) => p.card));
  const missing = blanks(boxes);
  const roundFull =
    grid &&
    (grid.competition.judgeSlots || []).every((s) =>
      ((panels.find((p) => Number(p.round) === Number(round)) || {}).slots || []).some((t) => t.slot === s)
    );

  // The overall result a tabulator checks: the recalled dancers (with round
  // award winners) for the recall; places, totals and qualifications for the
  // final results.
  const overallTable = (stage, given = null) => {
    const data = given || overallFor(stage);
    if (!data) return <div className="tb-overall-sum">Loading the overall result…</div>;
    const rowsAll = data.rows || [];
    if (stage === "recall") {
      // Every dancer by combined Irish points over the rounds before the
      // recall, a line under the last one recalled (Devin 2026-10-28; round
      // medals are not shown here). Dancers with no standing (did not dance,
      // failed to complete) follow the line.
      const before = preRecallRounds(row);
      const ipOf = (r, n) => (r.rounds || []).find((x) => x.round === n)?.keptTotal;
      const fmtIp = (v) => (v == null ? "–" : String(Math.round(Number(v) * 100) / 100));
      const combined = (r) => {
        const parts = before.map((n) => ipOf(r, n)).filter((v) => v != null);
        return parts.length ? parts.reduce((a, b) => a + Number(b), 0) : null;
      };
      const ordered = rowsAll
        .slice()
        .sort(
          (a, b) =>
            (a.recalled === b.recalled ? 0 : a.recalled ? -1 : 1) ||
            (a.recallStanding || 1e9) - (b.recallStanding || 1e9) ||
            Number(a.card) - Number(b.card)
        );
      const recalledCount = ordered.filter((r) => r.recalled).length;
      const cols = 4 + before.length + 1;
      return (
        <div className="tb-overall">
          <div className="tb-overall-sum">
            {recalledCount} recalled{data.dancedCounts?.[0] ? ` of ${data.dancedCounts[0].n} danced` : ""}
          </div>
          <table className="tb-recalltable">
            <thead>
              <tr>
                <th>Place</th>
                <th>#</th>
                <th>Name</th>
                <th>School</th>
                {before.map((n) => (
                  <th key={n} className="n">R{n} IP</th>
                ))}
                <th className="n">{before.map((n) => `R${n}`).join("+")} IP</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((r, i) => (
                <React.Fragment key={r.ref}>
                  {i === recalledCount && recalledCount > 0 && (
                    <tr className="tb-recallline">
                      <td colSpan={cols}>Recall line — {recalledCount} recalled above</td>
                    </tr>
                  )}
                  <tr className={r.recalled ? "recalled" : "out"}>
                    {/* FTC: no place (the results' rule), still listed */}
                    <td className="n">{r.ftc ? "" : r.recallStanding || ""}</td>
                    <td className="n">{r.card}</td>
                    <td>{r.label}</td>
                    <td className="muted">{r.schoolName}</td>
                    {before.map((n) => (
                      <td key={n} className="n">{fmtIp(ipOf(r, n))}</td>
                    ))}
                    <td className="n tot">{r.ftc ? "FTC" : fmtIp(combined(r))}</td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    const placedRows = rowsAll.filter((r) => r.position).sort((a, b) => a.position - b.position);
    const quals = (r) => [r.wq ? "WQ" : "", r.naidcQ ? "NAIDC" : ""].filter(Boolean).join(", ");
    return (
      <div className="tb-overall">
        <table>
          <thead>
            <tr><th>Place</th><th>#</th><th>Name</th><th>School</th><th>Total IP</th><th>Qualifies</th></tr>
          </thead>
          <tbody>
            {placedRows.map((r) => (
              <tr key={r.ref}>
                <td className="n">{r.position}{r.tied ? "T" : ""}</td>
                <td className="n">{r.card}</td>
                <td>{r.label}</td>
                <td className="muted">{r.schoolName}</td>
                <td className="n">{Number(r.totalPoints).toFixed(2)}</td>
                <td>{quals(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rowsAll.some((r) => r.ftc) && (
          <div className="tb-overall-sum">
            Failed to complete: {rowsAll.filter((r) => r.ftc).map((r) => `#${r.card}`).join(", ")}
          </div>
        )}
      </div>
    );
  };

  // The round awards as they are posted (Casey Costello's ECRO 2026 process,
  // Devin 2026-10-29): with the recall, each round's winners by card number,
  // no ranking (dancers know they won, not where); with the full result,
  // ranked from last to first by the drawn judge's mark. One table per round.
  // given: data loaded outside the check (the published recall, the done step)
  const roundAwardsBlock = (stage, given = null) => {
    const data = given || overallFor(stage);
    const ranked = stage === "results";
    const lists = ((data && data.roundAwardLists && (ranked ? data.roundAwardLists.ranked : data.roundAwardLists.byNumber)) || [])
      // with the recall, card order whatever the list's order (Fable review 6075036d R1-F3)
      .map((l) => ({ ...l, winners: ranked ? l.winners || [] : [...(l.winners || [])].sort((a, b) => Number(a.card) - Number(b.card)) }))
      .filter((l) => l.winners.length);
    if (!lists.length) return null;
    return (
      <div className="tb-overall tb-awards">
        <div className="tb-awards-head">
          Round awards — {ranked ? "from last to first, as the extra sheet ranks them" : "posted in numerical order with the recall, no ranking"}
        </div>
        <div className="tb-awards-cols">
          {lists.map((list) => (
            <div key={list.round} className="tb-awards-col">
              <div className="tb-overall-sum">
                Round {list.round}: {list.winners.length} award{list.winners.length === 1 ? "" : "s"}
              </div>
              <table>
                <thead>
                  <tr>
                    {ranked && <th>Place</th>}
                    <th>#</th>
                    <th>Name</th>
                    <th>School</th>
                  </tr>
                </thead>
                <tbody>
                  {list.winners.map((w) => (
                    <tr key={w.card}>
                      {ranked && <td className="n">{w.rank != null ? (w.tied ? `${w.rank}T` : ordinal(w.rank)) : ""}</td>}
                      <td className="n">{w.card}</td>
                      <td>{w.name}</td>
                      <td className="muted">{w.school}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Rule 12 plus the regional director's extra NAIDC qualifiers (the next
  // recalled dancers in place order; never past the recalled), as on the
  // Results screen (Devin 2026-10-29). An admin's alone: no device may set it
  // (NEVER_ROUTES). A change makes the check stale, so the result is checked
  // again with the new line.
  const changeNaidcExtra = async (n) => {
    try {
      startBusy();
      setError("");
      await setNaidcExtra(eventId, compId, n);
      ShowToastMessage("success", "NAIDC qualifiers updated");
      await refreshStatus();
      loadOverall(compId, "results");
    } catch (e) {
      setError(errorText(e, "Could not change the NAIDC qualifiers"));
    } finally {
      setBusy(false);
    }
  };
  const naidcControl = () => {
    const data = overallFor("results");
    const q = data && data.qualification;
    if (!q || !data.competition?.qualifier || row.resultsPublished) return null;
    const set = q.naidcExtraSet || 0;
    if (!(q.naidcLeftOut > 0 || set > 0)) return null;
    const left = `${q.naidcLeftOut} recalled ${q.naidcLeftOut === 1 ? "dancer does" : "dancers do"} not qualify for NAIDC under Rule 12.`;
    if (jobDevice) {
      return (
        <div className="tb-naidc">
          <span>{left}{set > 0 ? ` ${q.naidcExtra} added by the director.` : ""} Only an admin can add NAIDC qualifiers.</span>
        </div>
      );
    }
    return (
      <div className="tb-naidc">
        <span>
          {left} NAIDC {q.naidcCount}
          {q.naidcExtra > 0 ? ` + ${q.naidcExtra} added` : ""}. Director adds the next:
        </span>
        {/* steps the SAVED setting (naidcExtraSet), so a setting that no
            longer applies can still be cleared */}
        <button
          type="button"
          className="op-btn"
          disabled={busy || set <= 0}
          aria-label="One fewer NAIDC qualifier"
          onClick={() => changeNaidcExtra(Math.min(set - 1, q.naidcLeftOut))}
        >
          −
        </button>
        <b>{set}</b>
        <button
          type="button"
          className="op-btn"
          disabled={busy || set >= q.naidcLeftOut}
          aria-label="One more NAIDC qualifier"
          onClick={() => changeNaidcExtra(set + 1)}
        >
          +
        </button>
        {q.naidcExtra < set && (
          <span className="muted">
            only {q.naidcExtra} {q.naidcExtra === 1 ? "applies" : "apply"} — no more recalled dancers
          </span>
        )}
      </div>
    );
  };

  // fullTable: the Results tab shows the full results table above this, so
  // the results stage leaves out its own short table
  const stepPanel = (opts = {}) => {
    if (!row || !step) return null;
    // corrected after publishing (an admin correction): the public page still
    // shows the old version until it is published again
    if (step.republish && step.step === "scores") {
      return (
        <div className="tb-step warn">
          <div className="tb-step-text">
            <b>Corrected after publishing</b>
            <span>
              Round {step.round} has a changed sheet. Print it and have the judge sign it again, then publish the{" "}
              {row.resultsPublished ? "results" : "recall"} again — the public page shows the old version until then.
            </span>
          </div>
        </div>
      );
    }
    if (step.step === "recall" || step.step === "results") {
      const stage = step.step;
      const isRecall = stage === "recall";
      const checked = stageChecked(row, stage);
      const check = (row.checks && row.checks[stage]) || {};
      return (
        <div className="tb-step tb-stage">
          <div className="tb-stage-head">
            <div className="tb-step-text">
              <b>
                {step.republish
                  ? isRecall
                    ? "Corrected recall — check it and publish it again"
                    : "Corrected results — check them and publish them again"
                  : isRecall
                  ? "Rounds 1 and 2 are signed off — check the recall"
                  : "Every sheet is signed off — check the results"}
              </b>
              <span>
                {checked
                  ? isRecall
                    ? "Print the recall list, then publish it. Publishing puts it on the public results page and opens round 3 check-in."
                    : "Print the announcer's pages (World qualifiers, then places from last to first). Publish once the results have been announced."
                  : "A tabulator looks over the result below and signs it off. Print and Publish unlock once it is checked."}
              </span>
            </div>
            {!canCheck ? null : isRecall ? (
              <>
                <button type="button" className="op-btn" disabled={busy || !checked} onClick={() => printStage(stage)}>
                  <Msr>print</Msr> Print recall list
                </button>
                {roundAwardsApply(row.competition) && (
                  <button type="button" className="op-btn" disabled={busy || !checked} onClick={() => printStage(stage, "roundawards")}>
                    <Msr>military_tech</Msr> Print round awards
                  </button>
                )}
              </>
            ) : (
              <>
                <button type="button" className="op-btn" disabled={busy || !checked} onClick={() => printStage(stage, "announcer")}>
                  <Msr>campaign</Msr> Print for announcer
                </button>
                <button type="button" className="op-btn" disabled={busy || !checked} onClick={() => printStage(stage)}>
                  <Msr>print</Msr> Print full results
                </button>
                {roundAwardsApply(row.competition) && (
                  <button type="button" className="op-btn" disabled={busy || !checked} onClick={() => printStage(stage, "roundawards")}>
                    <Msr>military_tech</Msr> Print round awards ranked
                  </button>
                )}
              </>
            )}
            {can(isRecall ? "ox.recall.publish" : "ox.results.publish") && (
            <button type="button" className="op-btn go" disabled={busy || !checked} onClick={() => setPublishAsk(stage)}>
              <Msr>campaign</Msr>{" "}
              {isRecall
                ? step.republish
                  ? "Publish recall list again"
                  : "Publish recall list"
                : step.republish
                ? "Publish results again"
                : "Publish results"}
            </button>
            )}
          </div>
          {/* the actions first (Devin 2026-10-28), then what is being checked */}
          <div className="tb-checkline">
            {checked ? (
              <span className="tb-done">
                <Msr>verified</Msr> Checked by {check.by || "a tabulator"}
                {check.at ? ` at ${new Date(check.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : ""}
              </span>
            ) : !canCheck ? (
              <span>Not checked yet.</span>
            ) : (
              <>
                {check.stale && (
                  <span className="tb-stale">Something changed after the last check — check it again.</span>
                )}
                <button
                  type="button"
                  className="op-btn go"
                  disabled={busy || !overallFor(stage) || (opts.fullTable && stage === "results" && !previewShown)}
                  onClick={() => checkStage(stage)}
                >
                  <Msr>fact_check</Msr> I've checked this {isRecall ? "recall" : "result"}
                </button>
              </>
            )}
          </div>
          {stage === "results" && naidcControl()}
          {canCheck ? (
            opts.fullTable && stage === "results" ? null : overallTable(stage)
          ) : (
            <div className="tb-overall-sum">A tabulator whose job includes Check results looks over the overall result here.</div>
          )}
          {canCheck && roundAwardsBlock(stage)}
        </div>
      );
    }
    if (step.step === "done") {
      return (
        <div className="tb-step">
          <div className="tb-step-text">
            <span className="tb-done"><Msr>check_circle</Msr> Results published</span>
            <span>
              {jobDevice
                ? "To fix a mistake found later, unpublish the results (Status screen), change the sheet, then check and publish again."
                : "A mistake found later can be corrected by an admin: open the judge's sheet and save the change."}
            </span>
          </div>
          {canCheck && (
            <>
              <button type="button" className="op-btn" disabled={busy} onClick={() => printStage("results", "announcer")}>
                <Msr>campaign</Msr> Print for announcer
              </button>
              <button type="button" className="op-btn" disabled={busy} onClick={() => printStage("results")}>
                <Msr>print</Msr> Print full results
              </button>
              {roundAwardsApply(row.competition) && (
                <button type="button" className="op-btn" disabled={busy} onClick={() => printStage("results", "roundawards")}>
                  <Msr>military_tech</Msr> Print round awards ranked
                </button>
              )}
            </>
          )}
          {canCheck && doneData && doneData.compId === String(compId) && roundAwardsBlock("results", doneData.data)}
        </div>
      );
    }
    if (row.recallPublished && step.step === "scores" && step.round >= 3) return recallPublishedPanel(step.round);
    return null;
  };

  // The recall is out: print the next round's judging sheets (recalled
  // dancers only) and, for the stage, the recall list.
  const recallPublishedPanel = (nextRound) => (
    <div className="tb-step">
      <div className="tb-step-text">
        <span className="tb-done"><Msr>check_circle</Msr> Recall published</span>
        <span>
          Print the judges' round {nextRound} score sheets, the round {nextRound} check-in and the musician sheet
          (recalled dancers only), then enter the scores.
        </span>
      </div>
      {can("ox.print.sheets") && (
        <>
          <button type="button" className="op-btn" disabled={busy} onClick={() => printScoreSheets(nextRound)}>
            <Msr>print</Msr> Print round {nextRound} score sheets
          </button>
          <button type="button" className="op-btn" disabled={busy} onClick={() => printRoundSheet("checkin", nextRound)}>
            <Msr>print</Msr> Print round {nextRound} check-in
          </button>
          <button type="button" className="op-btn" disabled={busy} onClick={() => printRoundSheet("musician", nextRound)}>
            <Msr>music_note</Msr> Print round {nextRound} musician sheet
          </button>
        </>
      )}
      {canCheck && (
        <>
          <button type="button" className="op-btn" disabled={busy} onClick={() => printStage("recall")}>
            <Msr>print</Msr> Print recall list
          </button>
          {roundAwardsApply(row.competition) && (
            <button type="button" className="op-btn" disabled={busy} onClick={() => printStage("recall", "roundawards")}>
              <Msr>military_tech</Msr> Print round awards
            </button>
          )}
        </>
      )}
    </div>
  );

  // The Results tab: the check, the announcer and full prints and Publish
  // (once published, the prints) at the top, then the full table as the
  // public page shows it.
  const resultsPanel = () => {
    if (!row || !step) return null;
    if (!resultsReady) {
      return <div className="op-empty">The results open here once every sheet of every round is signed off.</div>;
    }
    return (
      <>
        {stepPanel({ fullTable: true })}
        {!previewFor ? (
          <div className="tb-overall-sum">Loading the results…</div>
        ) : previewFor.error ? (
          <div className="op-error">{previewFor.error}</div>
        ) : (
          <div className="oir er tb-results">
            <div className="tb-results-head">
              {previewFor.data.published ? "Published results" : "Results — not yet published"}
            </div>
            <FullTable detail={previewFor.data} mineCards={NO_CARDS} seats={null} />
          </div>
        )}
      </>
    );
  };

  // The Recall tab: the recall's check / print / publish (and the recall
  // table) once rounds 1 and 2 are signed off; once published, the prints
  // that follow it, with the recall table still under them (Devin 2026-10-28).
  const recallPanel = () => {
    if (!row || !step) return null;
    if (step.step === "recall") return stepPanel();
    if (row.recallPublished) {
      const data = recallData && recallData.compId === String(compId) ? recallData.data : null;
      return (
        <>
          {recallPublishedPanel(preRecallRounds(row).length + 1)}
          {data ? overallTable("recall", data) : <div className="tb-overall-sum">Loading the recall…</div>}
          {data && roundAwardsBlock("recall", data)}
        </>
      );
    }
    return (
      <div className="op-empty">The recall opens here once every round 1 and round 2 sheet is signed off.</div>
    );
  };

  // A round's tab: every judge's scores side by side in running order, each
  // judge's sheet state under their name (Devin 2026-10-28). A judge's name
  // opens their sheet to edit; judges not yet on the round are added below.
  const fmtScore = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? String(Math.round(n * 100) / 100) : String(v);
  };
  const roundSeats = () =>
    (grid?.competition?.judgeSlots || [])
      .map((slot) => ({
        slot,
        seat: ((panels.find((p) => Number(p.round) === Number(round)) || {}).slots || []).find((t) => t.slot === slot),
      }))
      .filter((s) => s.seat);
  // Irish points on a round's tab (Devin 2026-10-28): each judge's points
  // under their score, the dropped high and low in grey, the dancer's round
  // total. From the live result (GET .../results), reloaded with the grid.
  const roundIpRow = (ref) => {
    const rows =
      roundResults && roundResults.compId === String(compId) && roundResults.grid === grid ? roundResults.data.rows || [] : [];
    const row_ = rows.find((x) => String(x.ref) === String(ref));
    return (row_?.rounds || []).find((x) => x.round === round) || null;
  };
  const markIp = (ref, slot) => {
    const rr = roundIpRow(ref);
    const j = (rr?.judges || []).find((x) => x.slot === slot);
    return j ? { ...j, dropped: droppedSlots(rr)[slot] || null } : null;
  };
  const roundIp = (ref) => {
    const rr = roundIpRow(ref);
    if (!rr) return <span className="tb-noscore">–</span>;
    if (rr.noShow) return "NS";
    return rr.keptTotal != null ? fmtScore(rr.keptTotal) : <span className="tb-noscore">–</span>;
  };
  const scoreTable = () => {
    const seats = roundSeats();
    if (seats.length === 0) {
      return <div className="op-muted">No judge's sheet entered for round {round} yet — add a judge below.</div>;
    }
    return (
      <div className="tb-table-wrap">
        <table className="tb-scores">
          <thead>
            <tr>
              <th className="n">#</th>
              {seats.map(({ slot, seat }) => {
                // Every seated sheet not yet signed off can be printed and
                // signed. One with nothing stored (everyone left as did not
                // dance, or a cleared sheet) says so, so it isn't mistaken for
                // a typed sheet (review d6198824 R1-F2).
                const st = sheetState(row, round, slot);
                const signed = st === "signed_off";
                const label = signed
                  ? "Signed off"
                  : st === "missing"
                  ? "No scores — print sign-off"
                  : SHEET_LABEL[st] || "Print sign-off";
                const personId = String(seat.person_id);
                return (
                  <th key={slot}>
                    <div className="tb-jhead">
                      <button type="button" className="tb-jname" disabled={busy} title="Edit this judge's scores" onClick={() => pickJudge(personId)}>
                        {judgeName(seat.person_id) || `Seat ${slot}`}
                      </button>
                      <span className={`tb-jstate ${st}`}>
                        <Msr>{signed ? "check_circle" : "radio_button_unchecked"}</Msr>
                        {label}
                      </span>
                      {/* the sheet's next step: print the sign-off sheet, then mark it signed */}
                      {!signed && (
                        <span className="tb-jacts">
                          <button
                            type="button"
                            className={`tb-jact${st !== "printed" ? " go" : ""}`}
                            disabled={busy}
                            onClick={() => printSheet(slot, personId)}
                          >
                            <Msr>print</Msr> {st === "printed" ? "Print again" : "Print sign-off"}
                          </button>
                          {st === "printed" && (
                            <button type="button" className="tb-jact go" disabled={busy} onClick={() => markSigned(slot)}>
                              <Msr>verified</Msr> Signed off
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
              <th className="tot">
                Round IP{grid.competition.dropHighLow ? <small>high &amp; low dropped</small> : null}
              </th>
            </tr>
          </thead>
          <tbody>
            {grid.roster.map((r) => (
              <tr key={r.ref}>
                <td className="n">
                  {r.card}
                  {r.mishap?.zeroed && <span className="tb-ftcmark">FTC</span>}
                </td>
                {seats.map(({ slot }) => {
                  const m = markIp(r.ref, slot);
                  return (
                    <td key={slot} className={`sc${m?.dropped ? " dropped" : ""}`} title={m?.dropped ? `Dropped (${m.dropped})` : undefined}>
                      {r.cells[slot] ? fmtScore(r.cells[slot].score) : <span className="tb-noscore">–</span>}
                      {r.cells[slot] && m && m.points != null && <small className="tb-ip">{fmtScore(m.points)} IP</small>}
                    </td>
                  );
                })}
                <td className="sc tot">{roundIp(r.ref)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const leavePage = guard(() => navigate(`/oireachtas/manage?id=${eventId}`));

  return (
    <>
      {loading && <Loader />}
      <HeaderOrg />
      <div className="oir op-page tb-page">
        <div className="wrap">
          <button type="button" className="op-back" onClick={leavePage}>
            <Msr>arrow_back</Msr> {event?.name || "Event"}
          </button>
          <div className="ph">
            <div>
              {/* View Scores is the competition's hub; Enter Scores picks and types one judge's sheet */}
              <h1>{phase === "pick" && !entering ? "View Scores" : "Enter Scores"}</h1>
              <div className="sub">
                <span>
                  {phase !== "pick"
                    ? "Type the scores from the judge's sheet, then check them against the paper."
                    : entering
                    ? "Pick the competition, round and judge, then type the scores from their sheet."
                    : "Every judge's scores for the round. Click a judge to edit their sheet; print and sign off each sheet here."}
                </span>
              </div>
              {phase === "pick" && refreshed.lastAt && (
                <div className={`tb-updated${refreshed.failed ? " failed" : ""}`} role="status">
                  {refreshed.failed
                    ? `Couldn't refresh — showing ${clockLabel(refreshed.lastAt)}`
                    : `Updated ${clockLabel(refreshed.lastAt)} · refreshes every 10 seconds`}
                </div>
              )}
            </div>
          </div>

          {loadError && <div className="op-error">{loadError}</div>}

          {!loading && !loadError && (
            <>
              {phase === "pick" && (
                <div className="tb-pickers">
                  <label className="tb-field">
                    <span className="tb-label">{todayOnly ? "Competition — today's schedule" : "Competition"}</span>
                    <select className="tb-select" value={compId} disabled={busy} onChange={(e) => pickComp(e.target.value)}>
                      <option value="">Select…</option>
                      {/* the open competition stays listed (a Status-screen link to another day's) */}
                      {rows
                        .filter((r) => !todayOnly || todayIds.has(String(r.competition._id)) || String(r.competition._id) === compId)
                        .map((r) => (
                        <option key={r.competition._id} value={r.competition._id} disabled={!r.draw?.locked}>
                          {r.competition.code} — {r.competition.name} · {stepLabel(r)}
                        </option>
                        ))}
                    </select>
                  </label>
                  {todayIds && (
                    // the filter is a default, not a wall: past midnight, or a
                    // competition from another day (review a02c3146 R1-F1)
                    <button type="button" className="tb-showall" disabled={busy} onClick={() => setShowAll(!showAll)}>
                      {showAll ? "Only today's competitions" : "Show every competition"}
                    </button>
                  )}

                  {row && row.draw?.locked && (comp.rounds > 1 || !entering) && (
                    <div className="tb-field">
                      <span className="tb-label">Round</span>
                      <div className="tb-pills" role="tablist" aria-label="Round">
                        {Array.from({ length: comp.rounds }, (_, i) => i + 1).map((r) => (
                          <React.Fragment key={r}>
                            {/* the recall sits between the rounds it is made from and the recall round */}
                            {!entering && comp.rounds >= 3 && r === preRecallRounds(row).length + 1 && (
                              <button
                                type="button"
                                role="tab"
                                aria-selected={showRecall}
                                className={`op-chip${showRecall ? " on" : ""}`}
                                disabled={busy || !(step?.step === "recall" || row.recallPublished)}
                                title={step?.step === "recall" || row.recallPublished ? "" : "Opens once rounds 1 and 2 are signed off"}
                                onClick={pickRecall}
                              >
                                Recall
                              </button>
                            )}
                            <button
                              type="button"
                              role="tab"
                              aria-selected={!showRecall && !showResults && r === round}
                              className={`op-chip${!showRecall && !showResults && r === round ? " on" : ""}`}
                              disabled={busy || !roundOpen(row, r)}
                              title={roundOpen(row, r) ? "" : "Opens once the recall is published"}
                              onClick={() => pickRound(r)}
                            >
                              Round {r}
                            </button>
                          </React.Fragment>
                        ))}
                        {/* the results after the last round (Devin 2026-10-28) */}
                        {!entering && (
                          <button
                            type="button"
                            role="tab"
                            aria-selected={showResults}
                            className={`op-chip${showResults ? " on" : ""}`}
                            disabled={busy || !resultsReady}
                            title={resultsReady ? "" : "Opens once every sheet is signed off"}
                            onClick={pickResults}
                          >
                            Results
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {error && <div className="op-error" role="alert">{error}</div>}
              {notice && (phase === "entry" || phase === "confirm") && (
                <div className="ci-banner" role="status">
                  <Msr>restore</Msr>
                  <span>{notice}</span>
                </div>
              )}
              {phase === "pick" &&
                (() => {
                  const drafts = listDrafts(window.localStorage, eventId);
                  return drafts.length > 0 ? (
                    <div className="ci-banner" role="status">
                      <Msr>restore</Msr>
                      <span>
                        <b>Unsaved scores waiting:</b>{" "}
                        {drafts.map((d) => `${d.code} round ${d.round} — ${d.judge}`).join("; ")}. Open that judge's
                        sheet to restore them.
                      </span>
                    </div>
                  ) : null;
                })()}

              {/* ---- Enter Scores: just the judges of the round, each opening their sheet ---- */}
              {phase === "pick" && entering && row && grid && (
                <div className="tb-field">
                  <span className="tb-label">Judge — round {round}</span>
                  <div className="tb-judges">
                    {pool.map((a) => {
                      const seat = seatOf(a._id);
                      const st = seat ? sheetState(row, round, seat) : "";
                      return (
                        <button
                          key={a._id}
                          type="button"
                          className={`tb-judge ${st}`}
                          disabled={busy || (roundFull && !seat)}
                          onClick={() => pickJudge(String(a._id))}
                        >
                          <b>{a.name}</b>
                          <span>{seat ? SHEET_LABEL[st] : roundFull ? "Round panel is full" : "Not entered"}</span>
                        </button>
                      );
                    })}
                  </div>
                  {pool.length === 0 && (
                    <div className="op-muted">No judges on this event yet — add them to the event's adjudicator list.</div>
                  )}
                  <button type="button" className="tb-showall" disabled={busy} onClick={showHub}>
                    View Scores for this competition
                  </button>
                </div>
              )}

              {/* ---- View Scores: the Recall tab ---- */}
              {phase === "pick" && showRecall && recallPanel()}

              {/* ---- View Scores: the Results tab ---- */}
              {phase === "pick" && showResults && resultsPanel()}

              {/* ---- View Scores: a round's tab — only that round's scores (Devin
                   2026-10-28); the recall and the results have their own tabs ---- */}
              {phase === "pick" && !entering && !showRecall && !showResults && row && grid && (
                <div className="tb-field">
                  <span className="tb-label">Round {round} — every judge</span>
                  {scoreTable()}
                  {!roundFull && (
                    <>
                      <span className="tb-label tb-addlabel">Enter scores for the next judge — round {round}</span>
                      <div className="tb-judges">
                        {pool
                          .filter((a) => !seatOf(a._id))
                          .map((a) => (
                            <button
                              key={a._id}
                              type="button"
                              className="tb-judge"
                              disabled={busy}
                              onClick={() => pickJudge(String(a._id))}
                            >
                              <b>{a.name}</b>
                              <span>Not entered</span>
                            </button>
                          ))}
                      </div>
                    </>
                  )}
                  {pool.length === 0 && (
                    <div className="op-muted">No judges on this event yet — add them to the event's adjudicator list.</div>
                  )}
                </div>
              )}

              {/* ---- entry: the boxes laid out like the paper sheet ---- */}
              {phase === "entry" && (
                <>
                  <div className="tb-sheethead" ref={sheetHeadRef}>
                    <h2>{judgeName(person)}</h2>
                    <span>
                      {comp.code} — {comp.name} · Round {round}
                    </span>
                  </div>
                  {changeJudgeBar()}
                  {roundPublished(row, round) && (
                    <div className="ci-banner" role="alert">
                      <Msr>warning</Msr>
                      <span>
                        <b>
                          {row.resultsPublished ? "These results are published." : "This round's recall is published."}
                        </b>{" "}
                        Any change here corrects a public result. Only do it to fix a mistake that was missed —
                        you'll be asked to confirm, the judge's sheet must be signed again, and the{" "}
                        {row.resultsPublished ? "results" : "recall"} published again.
                      </span>
                    </div>
                  )}
                  {sheetPages(boxes).map((page, p, all) => (
                    <div key={p} className="tb-sheetpage">
                      {all.length > 1 && <div className="tb-pagelabel">Page {p + 1} of {all.length}</div>}
                      <div className={`tb-columns${page.length === 1 ? " single" : ""}`}>
                        {page.map((col, c) => (
                          <div key={c} className="tb-col">
                            {col.map(({ box: b, index: i }) => (
                              <div key={b.card} className={`tb-box${b.ftc ? " ftc" : ""}${badCards.has(b.card) ? " bad" : ""}`}>
                                <label className="tb-card" htmlFor={`score-${b.card}`}>
                                  {b.card}
                                </label>
                                <input
                                  id={`score-${b.card}`}
                                  ref={(el) => (boxRefs.current[i] = el)}
                                  inputMode="decimal"
                                  autoComplete="off"
                                  value={b.score}
                                  onChange={(e) => editBox(i, { score: e.target.value })}
                                  onKeyDown={onBoxKey(i)}
                                  onFocus={(e) => {
                                    e.target.select();
                                    if (!openingFocusRef.current) centreBox(e.target);
                                  }}
                                />
                                <button
                                  type="button"
                                  tabIndex={-1}
                                  className="tb-ftc"
                                  aria-pressed={b.ftc}
                                  title="Failed to complete — scores zero for this round"
                                  onClick={() => editBox(i, { ftc: !b.ftc })}
                                >
                                  FTC
                                </button>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="tb-hint">Type a score and press Enter or Tab for the next box. Enter on the last box goes to the check.</div>
                </>
              )}

              {/* ---- confirm ---- */}
              {phase === "confirm" && (
                <>
                  <div className="tb-sheethead">
                    <h2>Check {judgeName(person)}'s scores</h2>
                    <span>
                      {comp.code} · Round {round} — compare with the paper sheet
                    </span>
                  </div>
                  {changeJudgeBar()}
                  {problems.length > 0 && (
                    <div className="op-error" role="alert">
                      {problems.map((p) => (
                        <div key={p.card}>{p.message}</div>
                      ))}
                    </div>
                  )}
                  {missing.length > 0 && (
                    <div className="ci-banner" role="status">
                      <Msr>info</Msr>
                      <span>
                        No score for {missing.map((c) => `#${c}`).join(", ")}. Leave a box blank only for a dancer
                        who didn't dance — anyone another judge scored must be on every judge's sheet.
                      </span>
                    </div>
                  )}
                  {scored === 0 && !boxes.some((b) => b.ftc) && (
                    <div className="ci-banner" role="status">
                      <Msr>info</Msr>
                      <span>
                        This sheet has no scores: every dancer is down as did not dance. Confirm to seat{" "}
                        {judgeName(person)} for it, then print and sign their sheet.
                      </span>
                    </div>
                  )}
                  {sheetPages(boxes).map((page, p, all) => (
                    <div key={p} className="tb-sheetpage">
                      {all.length > 1 && <div className="tb-pagelabel">Page {p + 1} of {all.length}</div>}
                      <div className={`tb-columns${page.length === 1 ? " single" : ""}`}>
                        {page.map((col, c) => (
                          <div key={c} className="tb-col">
                            {col.map(({ box: b }) => {
                              const blank = b.score.trim() === "";
                              return (
                                <div
                                  key={b.card}
                                  className={`tb-crow${b.ftc ? " ftc" : blank ? " blank" : ""}${badCards.has(b.card) ? " bad" : ""}`}
                                >
                                  <span className="tb-card">{b.card}</span>
                                  <b>{b.ftc ? "FTC" : blank ? "no score" : b.score.trim()}</b>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {!compId && phase === "pick" && <div className="op-empty">Select a competition to start.</div>}
            </>
          )}
        </div>

        {/* ---- bottom bar ---- */}
        {phase === "entry" && (
          <div className="actionbar">
            <div className="actionbar-in tb-bar">
              <button type="button" className="ab-back" onClick={guard(backToPick)}>
                <Msr>arrow_back</Msr> {entering ? "Judges" : "View Scores"}
              </button>
              <span className="tb-bar-status">
                {scored} of {boxes.length} scored
              </span>
              <button type="button" className="ab-next" onClick={toConfirm}>
                Save <Msr>arrow_forward</Msr>
              </button>
            </div>
          </div>
        )}
        {phase === "confirm" && (
          <div className="actionbar">
            <div className="actionbar-in tb-bar">
              <button type="button" className="ab-back" disabled={busy} onClick={() => setPhase("entry")}>
                <Msr>edit</Msr> Back to edit
              </button>
              <span className="tb-bar-status">
                {scored} of {boxes.length} scored
              </span>
              <button type="button" className="ab-next" disabled={busy || problems.length > 0} onClick={() => confirmSheet()}>
                Confirm <Msr>check</Msr>
              </button>
            </div>
          </div>
        )}

        {/* ---- a device can't correct a published result: unpublish first ---- */}
        {correctionAsk && jobDevice && (
          <div className="modal-scrim" role="dialog" aria-modal="true">
            <div className="omodal">
              <div className="modal-ic">
                <Msr>lock</Msr>
              </div>
              <h3>This result is published</h3>
              <p>
                The {comp?.code} {row?.resultsPublished ? "results are" : "recall is"} already public, and this changes
                round {round}. To fix it, unpublish the {row?.resultsPublished ? "results" : "recall"} first (Status
                screen), then change the sheet, check it and publish again.
              </p>
              <div className="modal-act">
                <button type="button" className="mbtn ghost" onClick={() => setCorrectionAsk(false)}>
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ---- correcting a published result ---- */}
        {correctionAsk && !jobDevice && (
          <div className="modal-scrim" role="dialog" aria-modal="true">
            <div className="omodal">
              <div className="modal-ic">
                <Msr>warning</Msr>
              </div>
              <h3>Correct a published result?</h3>
              <p>
                The {comp?.code} {row?.resultsPublished ? "results are" : "recall is"} already public, and this changes
                round {round}. Only do this to fix a mistake that went unnoticed through entry, checking and sign-off.
              </p>
              <p>
                After saving: {judgeName(person)}'s sheet must be printed and signed again, and the{" "}
                {row?.resultsPublished ? "results" : "recall"} published again — the public page keeps the old version
                until then. The change is recorded under your admin login.
              </p>
              <label className="tb-ack">
                <input type="checkbox" checked={correctionOk} onChange={(e) => setCorrectionOk(e.target.checked)} /> I
                understand this changes a published result
              </label>
              <div className="modal-act">
                <button type="button" className="mbtn ghost" disabled={busy} onClick={() => setCorrectionAsk(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="mbtn danger"
                  disabled={busy || !correctionOk}
                  onClick={() => confirmSheet(true)}
                >
                  Save the correction
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ---- publish confirmation ---- */}
        {publishAsk && (
          <div className="modal-scrim" role="dialog" aria-modal="true">
            <div className="omodal">
              <div className="modal-ic">
                <Msr>campaign</Msr>
              </div>
              <h3>
                Publish the {publishAsk === "recall" ? "recall list" : "results"} for {comp?.code}?
              </h3>
              <p>
                {publishAsk === "recall"
                  ? "It goes on the public results page straight away, and round 3 check-in opens for the recalled dancers."
                  : "Families see them on the public results page straight away. Publish once the results have been announced."}
              </p>
              <div className="modal-act">
                <button type="button" className="mbtn ghost" disabled={busy} onClick={() => setPublishAsk("")}>
                  Cancel
                </button>
                <button type="button" className="mbtn danger" disabled={busy} onClick={() => publish(publishAsk)}>
                  Publish
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <ToastContainer />
    </>
  );
};

export default OireachtasTabulation;
